import axiosInstance from "@/api/axios-instance";

export type TestUserMode = "EXCLUDE" | "INCLUDE" | "ONLY";

export type LifecycleSegment =
  | "LEAD"
  | "NEW"
  | "ACTIVE"
  | "INACTIVE"
  | "AT_RISK"
  | "CHURNED";

/**
 * Broadcast audience filters. Within a dimension the values are OR'd; across
 * dimensions they are AND'd. Spam + lead users are always excluded; test users
 * follow `test_users` (default EXCLUDE). Explicitly picked `user_ids` are always
 * included (unioned with the filter matches).
 */
export interface BroadcastFilters {
  risk_category?: string[];
  behavior_tags?: string[];
  tiers?: string[];
  hrc_status?: string[];
  mc_status?: string[];
  user_type?: string[];
  region?: string;
  test_users?: TestUserMode;
  user_ids?: string[];
  lifecycle?: LifecycleSegment[];
}

export interface PreviewUser {
  id: string;
  full_name: string | null;
  phone: string | null;
  email: string | null;
}

export interface UserSearchResult {
  id: string;
  full_name: string;
  phone: string | null;
}

export interface CreateBroadcastInput extends BroadcastFilters {
  title: string;
  body: string;
  type: string;
  path: string;
}

export type BroadcastFrequency = "DAILY" | "WEEKLY" | "MONTHLY";

/** Structured recurrence; all times are IST. */
export interface BroadcastRecurrence {
  frequency: BroadcastFrequency;
  /** "HH:mm" (24h). */
  time: string;
  /** WEEKLY: 0=Sun … 6=Sat. */
  weekdays?: number[];
  /** MONTHLY: 1–31. */
  day_of_month?: number;
}

export type ScheduledBroadcastKind = "ONCE" | "RECURRING";
export type ScheduledBroadcastStatus =
  | "SCHEDULED"
  | "PAUSED"
  | "COMPLETED"
  | "CANCELLED"
  | "FAILED";

export interface CreateScheduledBroadcastInput extends CreateBroadcastInput {
  kind: ScheduledBroadcastKind;
  /** ISO datetime — for a ONCE schedule. */
  scheduled_at?: string;
  recurrence?: BroadcastRecurrence;
  /** ISO datetime — optional stop for a recurring schedule. */
  end_at?: string;
}

export interface ScheduledBroadcastRecord {
  id: string;
  title: string;
  body: string;
  type: string;
  path: string;
  filters: BroadcastFilters;
  kind: ScheduledBroadcastKind;
  scheduled_at: string | null;
  recurrence: BroadcastRecurrence | null;
  end_at: string | null;
  next_run_at: string | null;
  last_run_at: string | null;
  run_count: number;
  status: ScheduledBroadcastStatus;
  created_by_clerk_id: string | null;
  created_at: string;
}

export interface BroadcastResult {
  broadcastId: string;
  matchedUsers: number;
  recipients: number;
  /** Matched users OneSignal didn't recognize (no registered device). */
  invalidAliases: number;
  status: "SENT" | "PARTIAL" | "FAILED";
}

export interface BroadcastRecord {
  id: string;
  title: string;
  body: string;
  type: string;
  path: string;
  filters: BroadcastFilters;
  matched_count: number;
  recipients_count: number | null;
  status: "SENT" | "PARTIAL" | "FAILED";
  error: string | null;
  sent_by_clerk_id: string | null;
  created_at: string;
}

export async function previewBroadcast(
  filters: BroadcastFilters,
): Promise<{ matchedUsers: number }> {
  const response = await axiosInstance.post("/broadcasts/preview", filters);
  return response.data;
}

export async function getSegmentCounts(
  filters: BroadcastFilters,
): Promise<{ counts: Record<LifecycleSegment, number> }> {
  const response = await axiosInstance.post(
    "/broadcasts/segment-counts",
    filters,
  );
  return response.data;
}

export interface PreviewUsersResult {
  matchedUsers: number;
  users: PreviewUser[];
  page: number;
  limit: number;
  hasMore: boolean;
}

export async function previewBroadcastUsers(
  filters: BroadcastFilters,
  page = 0,
  limit = 100,
): Promise<PreviewUsersResult> {
  const response = await axiosInstance.post(
    "/broadcasts/preview-users",
    filters,
    { params: { page, limit } },
  );
  return response.data;
}

/** Search users for the hand-pick dropdown (reuses the users list endpoint). */
export async function searchUsers(
  query: string,
): Promise<UserSearchResult[]> {
  const response = await axiosInstance.get("/users", {
    params: { page: 0, search: query },
  });
  const users = response.data?.users ?? [];
  return users.map((u: any) => ({
    id: u.id,
    full_name: u.full_name,
    phone:
      u.preferred_phone_number ??
      u.otpless_identifier ??
      (Array.isArray(u.phone_numbers) ? u.phone_numbers[0] : null) ??
      null,
  }));
}

export async function createBroadcast(
  input: CreateBroadcastInput,
): Promise<BroadcastResult> {
  const response = await axiosInstance.post("/broadcasts", input);
  if (!response.status.toString().startsWith("2")) {
    throw new Error("Failed to send broadcast");
  }
  return response.data;
}

export async function getBroadcasts(): Promise<BroadcastRecord[]> {
  const response = await axiosInstance.get("/broadcasts");
  return response.data;
}

export async function createScheduledBroadcast(
  input: CreateScheduledBroadcastInput,
): Promise<ScheduledBroadcastRecord> {
  const response = await axiosInstance.post("/broadcasts/scheduled", input);
  return response.data;
}

export async function getScheduledBroadcasts(): Promise<
  ScheduledBroadcastRecord[]
> {
  const response = await axiosInstance.get("/broadcasts/scheduled");
  return response.data;
}

export async function updateScheduledBroadcast(
  id: string,
  status: ScheduledBroadcastStatus,
): Promise<ScheduledBroadcastRecord> {
  const response = await axiosInstance.patch(`/broadcasts/scheduled/${id}`, {
    status,
  });
  return response.data;
}

export async function cancelScheduledBroadcast(
  id: string,
): Promise<ScheduledBroadcastRecord> {
  const response = await axiosInstance.delete(`/broadcasts/scheduled/${id}`);
  return response.data;
}
